import { useCallback, useEffect, useState } from "react";
import { Loader2, Check, X } from "lucide-react";
import { api, money } from "../lib/api";

export const padNum = (n) => `#${String(n).padStart(4, "0")}`;

export const NumberPicker = ({ edition, editions, onEdition, value, onChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preferred, setPreferred] = useState("");
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/registry-numbers?edition=${edition}&limit=60`);
      setData(data);
      if (!data.available.includes(value)) onChange(data.next_available ?? null);
    } finally {
      setLoading(false);
    }
    // onChange/value intentionally excluded: reloading only depends on the chosen edition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edition]);

  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    const n = parseInt(preferred, 10);
    if (!n || n < 1) return;
    setChecking(true);
    setCheck(null);
    try {
      const { data } = await api.get(`/registry-numbers/check?number=${n}`);
      setCheck(data);
      if (data.available) {
        if (data.edition !== edition) onEdition(data.edition);
        onChange(data.number);
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div data-testid="number-picker">
      <div className="hy-label mb-4">Edition</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(editions || []).map((e) => (
          <button key={e.edition} type="button" onClick={() => onEdition(e.edition)}
            disabled={e.sold_out}
            data-testid={`edition-select-${e.edition}`}
            className={`text-left p-4 border transition-all duration-300 disabled:opacity-40 ${edition === e.edition ? "border-amber-400 bg-amber-500/10" : "border-amber-500/20 hover:border-amber-500/50"}`}>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-amber-300/80">{e.edition}</div>
            <div className="font-display text-lg text-white mt-2">
              {e.price_from !== e.price_to ? `from ${money(e.price_from)}` : money(e.price)}
            </div>
            <div className="text-[0.65rem] text-slate-500 mt-1">
              {padNum(e.range_start)}–{e.range_end ? padNum(e.range_end) : "∞"}
            </div>
            <div className="text-[0.65rem] text-slate-500 mt-1">
              {e.sold_out ? "Fully claimed" : e.remaining === null ? "Open series" : `${e.remaining} available`}
            </div>
          </button>
        ))}
      </div>

      <div className="hy-label mt-9 mb-3">Preferred registry number</div>
      <div className="flex flex-wrap gap-2">
        <input value={preferred} onChange={(e) => setPreferred(e.target.value.replace(/\D/g, ""))}
          className="hy-input max-w-[10rem]" placeholder="0209" data-testid="preferred-number-input" />
        <button type="button" onClick={runCheck} disabled={checking || !preferred}
          className="hy-btn-ghost px-5 text-xs uppercase flex items-center gap-2 disabled:opacity-50"
          data-testid="check-availability-btn">
          {checking && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Check Availability
        </button>
      </div>
      {check && (
        <div className={`flex items-center gap-2 text-sm mt-3 ${check.available ? "text-emerald-300" : "text-red-300"}`}
          data-testid="availability-result">
          {check.available ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
          {padNum(check.number)} {check.available ? "AVAILABLE" : "NOT AVAILABLE"}
          <span className="text-slate-500 text-xs">· {check.edition}</span>
        </div>
      )}

      <div className="hy-label mt-9 mb-3">
        Available numbers · {edition}
        {data?.remaining !== null && data?.remaining !== undefined && (
          <span className="text-slate-500 normal-case tracking-normal"> ({data.remaining} left)</span>
        )}
      </div>
      {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-300" />}
      {!loading && data && (
        data.available.length === 0
          ? <p className="text-sm text-red-300" data-testid="edition-sold-out">This edition is fully claimed.</p>
          : (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto pr-1"
              data-testid="available-numbers">
              {data.available.map((n) => (
                <button key={n} type="button" onClick={() => onChange(n)}
                  data-testid={`number-option-${n}`}
                  className={`py-2 text-xs font-mono-hy border transition-colors ${value === n ? "border-amber-400 bg-amber-500/15 text-amber-100" : "border-amber-500/20 text-slate-300 hover:border-amber-500/50"}`}>
                  {padNum(n)}
                </button>
              ))}
            </div>
          )
      )}
      {value && (
        <div className="text-sm text-slate-300 mt-5" data-testid="selected-number">
          Selected: <span className="text-amber-200 font-mono-hy">{padNum(value)}</span>
          {data?.prices?.[String(value)] && (
            <span className="text-slate-500"> · {money(data.prices[String(value)])}</span>
          )}
        </div>
      )}
    </div>
  );
};
